import InfoIcon from '@mui/icons-material/Info';
import { Box, Checkbox, FormControlLabel, FormHelperText, Tooltip } from '@mui/material';
import { alpha } from '@mui/material/styles';

import { AXLE_TEAL } from '../../constants/colors';
import { wrapOnClick } from '../../utils/clickEvent';
import { InputState, polymorphicIsEqual, useTransformedInputState } from '../../utils/inputState';
import { arrayWithValue, arrayWithoutValue } from '../../utils/iteration';
import { ReturnComponent } from '../../utils/reactTypes';
import { If } from '../utils/conditionalComponents';
import { HorizontalStack, VerticalStack } from '../utils/linearLayouts';
import { FieldWithAxleLabel } from './AxleLabel';

export const AxleCheckboxFieldSimple = ({
    label,
    enabled = true,
    value,
    setValue,
}: {
    label: string;
    enabled?: boolean;
    value: boolean;
    setValue: (value: boolean) => void;
}): ReturnComponent => {
    return (
        <FormControlLabel
            label={label}
            disabled={!enabled}
            control={
                <Checkbox
                    checked={value}
                    onClick={wrapOnClick(() => setValue(!value))}
                    color={'primary'}
                />
            }
        />
    );
};

export const AxleCheckboxCard = ({
    label,
    enabled = true,
    value,
    setValue,
}: {
    label: string;
    enabled?: boolean;
    value: boolean;
    setValue: (value: boolean) => void;
}): ReturnComponent => {
    return (
        <FormControlLabel
            label={label}
            disabled={!enabled}
            sx={{
                flex: 1,
                margin: 0,
                padding: 1,
                border: '1px solid',
                borderRadius: 1,
                borderColor: value ? 'primary.main' : 'divider',
                backgroundColor: value
                    ? (theme) => alpha(theme.palette.primary.main, 0.08)
                    : 'transparent',
                transition: 'border-color 0.2s ease, background-color 0.2s ease',
                cursor: enabled ? 'pointer' : 'not-allowed',
                '&:hover': {
                    borderColor: enabled ? 'primary.main' : undefined,
                },
            }}
            control={
                <Checkbox
                    checked={value}
                    onClick={wrapOnClick(() => setValue(!value))}
                    color='primary'
                />
            }
        />
    );
};

export const AxleCheckboxField = ({
    label = '',
    checkboxText,
    enabled = true,
    inputState,
    description,
}: {
    label?: string;
    checkboxText: string;
    enabled?: boolean;
    inputState: InputState<boolean>;
    description?: string;
}): ReturnComponent => {
    return (
        <Box mb={1}>
            <FieldWithAxleLabel label={label}>
                <HorizontalStack>
                    <FormControlLabel
                        label={checkboxText}
                        control={
                            <Checkbox
                                disabled={!enabled}
                                checked={inputState.value}
                                onClick={wrapOnClick(() =>
                                    inputState.setInputState((checked) => !checked),
                                )}
                            />
                        }
                    />
                    <If condition={!!description}>
                        <Tooltip title={description}>
                            <InfoIcon style={{ color: AXLE_TEAL, fontSize: '1em' }} />
                        </Tooltip>
                    </If>
                </HorizontalStack>
                <FormHelperText error={inputState.showError}>
                    {inputState.helperText}
                </FormHelperText>
            </FieldWithAxleLabel>
        </Box>
    );
};

export const AxleCheckboxesField = <Option, Value>({
    label,
    enabled = true,
    inputState,
    options,
    optionToValue,
    optionToLabel,
}: {
    label: string;
    enabled?: boolean;
    inputState: InputState<Value[]>;
    options: Option[];
    optionToValue: (option: Option) => Value;
    optionToLabel: (option: Option) => string;
}): ReturnComponent => {
    return (
        <FieldWithAxleLabel label={label}>
            <VerticalStack>
                {options.map((option) => (
                    <AxleCheckboxFieldSimple
                        key={optionToLabel(option)}
                        label={optionToLabel(option)}
                        enabled={enabled}
                        value={inputState.value.some((selectedValue) =>
                            // Object comparison doesn't work with '===' in Javascript.
                            polymorphicIsEqual(selectedValue, optionToValue(option)),
                        )}
                        setValue={(isChecked) => {
                            if (isChecked) {
                                inputState.setInputState(
                                    arrayWithValue(inputState.value, optionToValue(option)),
                                );
                            } else {
                                inputState.setInputState(
                                    arrayWithoutValue(inputState.value, optionToValue(option)),
                                );
                            }
                        }}
                    />
                ))}
            </VerticalStack>
            <FormHelperText error={inputState.showError}>{inputState.helperText}</FormHelperText>
        </FieldWithAxleLabel>
    );
};

export const useFlipBooleanInputState = (inputState: InputState<boolean>) =>
    useTransformedInputState({
        inputState: inputState,
        transform: (value) => !value,
        untransform: (value) => !value,
    });
